import React, { useEffect } from "react";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-gray-50 min-h-screen py-10 px-4 md:px-10">
      <div className="max-w-5xl mx-auto bg-white shadow-sm border border-gray-200 rounded-lg p-8 md:p-12 text-[15px] leading-relaxed text-gray-700">
        
        {/* Header */}
        <div className="border-b pb-6 mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2">PRIVACY POLICY</h1>
          <p className="text-sm font-medium text-red-600 mb-4">
            Please read this privacy policy carefully. By accessing or using the website, you agree to be bound by the terms described herein.
          </p>
          <p className="text-sm text-gray-500 italic">
            Disclaimer: In case of any discrepancy or difference, the English version will take precedence over any translation.
          </p>
          <p className="text-sm text-gray-600 mt-4 font-semibold">
            Last Updated: February 2026
          </p>
        </div>

        {/* 1. Content & Purpose */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            1. Content & Purpose
          </h2>
          <p className="mb-4">
            This privacy policy (“Privacy Policy”) applies to your use of the Mediiapp platform, owned and operated by <strong>Maya Enterprises</strong>, a wholesale dealer of kidney medicine located at GF-39, Omaxe Square, Jasola, New Delhi (hereinafter, referred to as “Maya Enterprises” or “We” or “Us”).
          </p>
          <p>
            The Website is a platform that facilitates the sale and distribution of pharmaceutical products and patient support services. By using our Services, you agree to the collection and hosting of your data as identified in this policy.
          </p>
        </section>

        {/* 2. Definitions */}
        <section className="mb-10 bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Key Definitions</h2>
          <ul className="space-y-3">
            <li>
              <strong>“Personal Information”</strong>: Any information that relates to a natural person which, directly or indirectly, is capable of identifying the person.
            </li>
            <li>
              <strong>“Sensitive Personal Data or Information”</strong>: Personal Information relating to physical, physiological, and mental health conditions; medical records and history; and biometric information provided to Maya Enterprises for storage or processing.
            </li>
          </ul>
        </section>

        {/* 3. Types of Information Collected */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            2. Types of Personal Information Collected
          </h2>
          <p className="mb-4">As part of the registration and order process, we may collect:</p>
          <div className="grid md:grid-cols-2 gap-4 ml-6 mb-4">
            <ul className="list-disc space-y-1">
              <li>Name & User ID</li>
              <li>Email & Phone Number</li>
              <li>Address (including ZIP/Postal code)</li>
            </ul>
            <ul className="list-disc space-y-1">
              <li>Gender & Age</li>
              <li>Prescription copies (Mandatory)</li>
              <li>Medical History relevant to transplants</li>
            </ul>
          </div>
          <p className="bg-yellow-50 p-3 text-sm border-l-4 border-yellow-400">
            <strong>Note:</strong> We may keep records of telephone calls received and made for inquiries, orders, or administration purposes.
          </p>
        </section>

        {/* 4. Purpose of Usage */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            3. Purposes for Which Information Is Used
          </h2>
          <ul className="list-disc ml-6 space-y-2">
            <li>Registration and identity verification of the User.</li>
            <li>Processing of orders for Kidney medications.</li>
            <li>Verification of medical prescriptions as required by the Drugs and Cosmetics Act.</li>
            <li>Technical administration and customization of the Website.</li>
            <li>Dealing with requests, enquiries, complaints, or disputes.</li>
          </ul>
        </section>

        {/* 5. Security Practices */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            4. Security Practices and Procedures
          </h2>
          <p>
            Maya Enterprises adopts reasonable security practices and procedures, including technical, operational, and physical control measures, to protect Personal Information from loss, misuse, and unauthorized access. While we strive to provide industry-standard security, due to the inherent vulnerabilities of the internet, we cannot ensure or warrant complete security of all information transmitted to us.
          </p>
        </section>

        {/* 6. User Rights */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            5. User Rights & Consent Withdrawal
          </h2>
          <p className="mb-4">
            Providing information is voluntary. You have the right to withdraw your consent at any time, but withdrawal will not be retroactive.
          </p>
          <p>
            You may access, modify, or delete your Personal Information by emailing us. In the event you withdraw consent, Maya Enterprises reserves the right to discontinue the services for which the said information was sought.
          </p>
        </section>

        {/* 7. Grievance Redressal */}
        <section className="mb-10 pt-8 border-t border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">6. Complaints and Grievance Redressal</h2>
          <p className="mb-6 text-gray-600">
            Maya Enterprises addresses discrepancies and grievances of all Users in a time-bound manner (within one month).
          </p>
          <div className="bg-green-50 p-6 rounded-md border border-green-100 flex flex-col md:flex-row justify-between gap-6">
            <div>
              <p className="font-bold text-green-800">Grievance Officer:</p>
              <p className="text-gray-900 font-semibold">Mr. Dheeraj Khatwani</p>
              <p>Director, Maya Enterprises</p>
              <p>GF-39, Omaxe Square, Jasola,</p>
              <p>New Delhi - 110025</p>
            </div>
            <div className="md:text-right">
              <p className="font-bold text-green-800">Contact Email:</p>
              <a href="mailto:Mediiappindia@gmail.com" className="text-green-600 font-bold hover:underline">
                Mediiappindia@gmail.com
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;